import { yupResolver } from '@hookform/resolvers/yup';
import type {
  DropDownResult,
  DynamicFormButtonSetting,
  ModalDialogButtonSetting,
  OdinKey,
} from '@myosh/odin-components';
import {
  Dialog,
  DynamicFormRequiredType,
  fieldValidationErrorMessage,
  HfInput,
  HfRichTextArea,
  useDeepEffect,
} from '@myosh/odin-components';
import { isNumber } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import type { HierarchyDropdownValue } from '../../../@types/hierarchy-fields';
import type { UserHierarchy, UserResult } from '../../../@types/users';
import useProfileData from '../../../hooks/use-profile-data';
import { useGetExternalUserConfigurationQuery } from '../../../redux/services/api';
import { useHierarchyTypesQuery } from '../../../redux/services/hierarchy';
import {
  useEditRecordCollaboratorMutation,
  useLazyGetCollaboratorEmailQuery,
  useLazyGetExternalUserEmailTemplateQuery,
  useSendContractorInvitationEmailMutation,
  useSendInvitationEmailMutation,
} from '../../../redux/services/record';
import { useGetExternalUsersQuery } from '../../../redux/services/user';
import { showError, showSuccess } from '../../../services/notification.service';
import { isEmptyObject } from '../../../utils/type-guards';
import { UserFields } from '../../../utils/user-config';
import { resolveUserValue } from '../../../utils/user-functions';
import { HfHierarchyField } from '../hierarchy/hf-hierarchy-field.component';
import type { Invitation } from '../invitation-field/invitation-field.component';

export interface ExternalUserEmailTemplate {
  id?: number;
  body: string;
  subject: string;
  recipient?: string;
  externalUserIds?: Array<number>;
  externalHierarchyId?: number;
}

export interface ExternalEmailInvitation {
  recordId: number;
  template: ExternalUserEmailTemplate;
}

interface InviteDialogProps {
  fieldId?: OdinKey;
  invitationEmail?: Invitation;
  recordId: number;
  visible: boolean;
  hide: () => void;
  updateLastChangeValue?: (lastChange: string) => void;
  setFormButtonsState?: React.Dispatch<React.SetStateAction<DynamicFormButtonSetting[]>>;
  readOnly?: boolean;
}

function InviteDialog({
  fieldId,
  invitationEmail,
  recordId,
  visible,
  hide,
  updateLastChangeValue,
  setFormButtonsState,
  readOnly,
}: InviteDialogProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getCollaboratorEmail] = useLazyGetCollaboratorEmailQuery();
  const [getExternaluserEmailTemplate] = useLazyGetExternalUserEmailTemplateQuery();
  const existingUserId = useRef<number | undefined>(undefined);
  const newTemplateRef = useRef<ExternalUserEmailTemplate | undefined>(undefined);
  const existingTemplateRef = useRef<ExternalUserEmailTemplate | undefined>(undefined);
  const prevEmailValue = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!visible) return;

    setIsLoading(true);

    if (invitationEmail?.collaboratorEmailId) {
      getCollaboratorEmail(invitationEmail.collaboratorEmailId)
        .unwrap()
        .then((response) => {
          setTemplateFields(response);
        })
        .finally(() => setIsLoading(false));
    } else {
      Promise.all([
        getExternaluserEmailTemplate({ type: 'new' }).unwrap(),
        getExternaluserEmailTemplate({ type: 'existing' }).unwrap(),
      ])
        .then(([newTemplate, existingTemplate]) => {
          newTemplateRef.current = newTemplate;
          existingTemplateRef.current = existingTemplate;
          const template = existingUserId.current != null ? existingTemplateRef.current : newTemplateRef.current;
          if (template) {
            setTemplateFields(template);
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [visible]);

  const { data: externalUsers } = useGetExternalUsersQuery(
    {
      fields: [
        UserFields.id,
        UserFields.firstName,
        UserFields.lastName,
        UserFields.fullName,
        UserFields.userId,
        UserFields.email,
        UserFields.userHierarchy,
      ],
    },
    { skip: !visible, pollingInterval: visible ? 10000 : undefined }
  );

  const { data: externalUserConfig } = useGetExternalUserConfigurationQuery();
  const externalHierarchyTypeId = externalUserConfig?.externalUserHierarchyType?.id;

  const { data: hierarchyTypes } = useHierarchyTypesQuery({ archived: 'false' });
  const updatedHierarchyTypes = hierarchyTypes
    ?.filter((type) => type.id === externalHierarchyTypeId)
    .map((hierarchyType) => ({
      ...hierarchyType,
      mandatoryForUserRecords: true,
    }));

  const [sendInvitation, { isLoading: postInviteLoading }] = useSendInvitationEmailMutation();
  const [sendContractorInvitation, { isLoading: postContractorInviteLoading }] =
    useSendContractorInvitationEmailMutation();
  const [editRecordCollaborator, { isLoading: editInviteLoading }] = useEditRecordCollaboratorMutation();

  const {
    profileData: { user },
  } = useProfileData();

  const schema = yup.object().shape({
    email: yup.string().email(t('email-validation')).required(t('email-required')),
    subject: yup.string().required(fieldValidationErrorMessage(t('subject'))),
    body: yup.string().required(fieldValidationErrorMessage(t('message'))),
    externalHierarchyId: yup.object().nullable(),
  });

  const { control, handleSubmit, watch, setValue, formState, reset } = useForm<FieldValues>({
    resolver: yupResolver(schema) as never,
    mode: 'onSubmit',
    defaultValues: {
      email: invitationEmail?.email,
      subject: '',
      body: '',
      externalHierarchyId: {},
    } as FieldValues,
  });

  const emailValue = watch('email');
  const matchedUser = externalUsers?.find((user) => user.email === emailValue);
  const isNewUser = !invitationEmail && !!emailValue && !matchedUser;
  const emailError = formState.errors.email;
  const showHierarchyField = isNewUser && !emailError;

  useDeepEffect(() => {
    if (emailValue) {
      const matchedUser = externalUsers?.find((user: UserResult) => user.email === emailValue);
      let hierarchyObj = {};
      if (matchedUser) {
        existingUserId.current = matchedUser.id;
        if (matchedUser && matchedUser.userHierarchy) {
          hierarchyObj = createExternalHierarchyObject(matchedUser.userHierarchy);
        }
        setValue('externalHierarchyId', hierarchyObj);
        setTemplateFields(existingTemplateRef.current);
      } else {
        existingUserId.current = undefined;
        if (prevEmailValue.current !== emailValue) {
          setValue('externalHierarchyId', {});
        }
        setTemplateFields(newTemplateRef.current);
      }
      prevEmailValue.current = emailValue;
    } else {
      setValue('externalHierarchyId', {});
    }
  }, [emailValue, externalUsers]);

  useEffect(() => {
    if (!readOnly) {
      setFormButtonsState?.((prev) =>
        prev.map((button) => ({
          ...button,
          disabled: postInviteLoading || postContractorInviteLoading || editInviteLoading,
        }))
      );
    }
  }, [postInviteLoading, postContractorInviteLoading, editInviteLoading, readOnly, setFormButtonsState]);

  useEffect(() => {
    if (invitationEmail) {
      setValue('email', invitationEmail?.email);
    }
  }, [invitationEmail, setValue]);

  const setTemplateFields = (template?: ExternalUserEmailTemplate) => {
    setValue('subject', replaceCurrentUserTextOnEmailsText(template?.subject || ''));
    setValue('body', replaceCurrentUserTextOnEmailsText(template?.body || ''));
  };

  const createExternalHierarchyObject = useCallback((userHierarchy: Array<UserHierarchy>) => {
    return Array.isArray(userHierarchy)
      ? userHierarchy.reduce(
          (acc: { [key: string]: HierarchyDropdownValue }, hierarchyItem) => {
            const typeCaption: string = hierarchyItem.hierarchyTypeCaption?.translations?.[0]?.value;
            if (typeCaption) {
              acc[typeCaption] = {
                id: hierarchyItem.id,
                caption: hierarchyItem.caption?.translations?.[0]?.value,
                hierarchyTypeId: hierarchyItem.hierarchyTypeId,
              };
            }
            return acc;
          },
          {} as { [key: string]: HierarchyDropdownValue }
        )
      : {};
  }, []);

  const replaceCurrentUserTextOnEmailsText = (message: string) => {
    return user ? message.replaceAll('{Current user}', `${resolveUserValue(user)}`) : message;
  };

  const onSubmit = (data: FieldValues) => {
    let externalHierarchyId;
    if (data['externalHierarchyId'] && !isEmptyObject(data['externalHierarchyId'])) {
      const externalHierarchy = data['externalHierarchyId'];
      const externalHierarchyKey = Object.keys(externalHierarchy)[0];
      externalHierarchyId = externalHierarchy[externalHierarchyKey].id;
    }
    if (invitationEmail) {
      const invitationData: ExternalUserEmailTemplate = {
        id: invitationEmail.id,
        recipient: data['email'],
        subject: data['subject'],
        body: data['body'],
        externalHierarchyId: externalHierarchyId,
      };

      setIsLoading(true);
      editRecordCollaborator(invitationData)
        .unwrap()
        .then(() => {
          hideModal();
          showSuccess(t('invitation-sent'));
        })
        .catch(() => {
          hide();
          showError(t('error-re-sending-invitation'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      const externalUsers: Array<DropDownResult> | undefined = data['externalUsers'];
      const externalUserIds = externalUsers?.map((user) => Number(user.value));
      const invitationData: ExternalEmailInvitation = {
        recordId: recordId,
        template: {
          id: isNumber(fieldId) ? fieldId : undefined,
          recipient: isNewUser ? data['email'] : '',
          externalUserIds: matchedUser && existingUserId.current ? [existingUserId.current] : [],
          subject: data['subject'],
          body: data['body'],
          externalHierarchyId: externalHierarchyId,
        },
      };
      setIsLoading(true);
      (isNumber(fieldId) ? sendInvitation(invitationData) : sendContractorInvitation(invitationData))
        .unwrap()
        .then((result) => {
          hideModal();
          showSuccess(t('invitation-sent'));
          updateLastChangeValue?.(result.lastChange);
          setTemplateFields(newTemplateRef.current);
        })
        .catch(() => {
          hide();
          if (externalUserIds && externalUserIds.length > 1) {
            showError(t('error-sending-invitation_other'));
          } else {
            showError(t('error-sending-invitation_one'));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const onError = (data: FieldValues) => console.error(data);

  const hideModal = () => {
    reset(newTemplateRef.current);
    existingUserId.current = undefined;
    newTemplateRef.current = undefined;
    hide();
  };

  const hasErrors = Object.keys(formState.errors).length > 0;

  const modalButtons: ModalDialogButtonSetting[] = [
    {
      name: 'invite',
      text: invitationEmail ? t('resend-invitation') : t('invite'),
      form: 'inviteForm',
      type: 'primary',
      disabled:
        isLoading || postInviteLoading || editInviteLoading || postContractorInviteLoading || hasErrors || !emailValue,
    },
  ];

  return (
    <>
      {visible && (
        <Dialog
          visible={visible}
          buttons={modalButtons}
          buttonType="submit"
          loading={isLoading}
          onClose={hideModal}
          containerClassName="w-[calc(100svw-2rem)] max-w-[600px] h-[calc(100svh-2rem)] !max-h-[550px]"
          closeOnClickOutside={false}
        >
          <form id="inviteForm" onSubmit={handleSubmit(onSubmit, onError)}>
            <HfInput
              control={control}
              name="email"
              label={t('email-address')}
              readOnly={!!invitationEmail}
              required={DynamicFormRequiredType.True}
              enableDebounce={false}
            />
            <div className={invitationEmail ? 'hidden' : ''}>
              <HfHierarchyField
                control={control}
                name="externalHierarchyId"
                isExternal={true}
                readOnly={showHierarchyField ? false : true}
                hierarchyTypes={updatedHierarchyTypes}
                isUserHierarchy={false}
                showAddNewOption={true}
              />
            </div>
            <HfInput control={control} name="subject" label={t('subject')} enableDebounce={false} />
            <HfRichTextArea control={control} name="body" label={t('message')} />
          </form>
        </Dialog>
      )}
    </>
  );
}

export default InviteDialog;
